<template>
  <!-- Native button; type/disabled state are normalized via computed props, and visual variant comes from btnClass -->
  <button class="new-base-btn" :class="btnClass" :type="typedButtonType" :form="form" :disabled="disabled"
    :aria-disabled="disabled">
    <!-- Label text, hidden (not removed) while loading so layout doesn't shift -->
    <span v-show="!isLoading" class="new-base-btn__name">{{ name }}</span>
    <!-- Spinner shown only while isLoading is true -->
    <div v-show="isLoading" class="new-base-btn__loader"></div>
    <!-- Default slot: used as the button content when no `name` text is given and it isn't loading (e.g. icon-only buttons) -->
    <slot v-if="!name && !isLoading" class="new-base-btn__slot"> </slot>
    <!-- Default slot again: forced to render whenever isIcon is true, even if `name` is also set -->
    <slot v-if="isIcon" class="new-base-btn__slot"> </slot>
  </button>
</template>

<script setup>
import { computed } from "vue";
/**
 * @prop {String} name - Button label text; when empty, the default slot is rendered instead.
 * @prop {String} type - Native button `type` attribute ('button' | 'submit' | 'reset'); anything else falls back to 'button'.
 * @prop {String} mode - Visual variant: '' (primary/default), 'bordered', 'text', or 'secondary'.
 * @prop {Boolean} disabled - Disables the button and applies the disabled visual state.
 * @prop {Boolean} isLoading - Shows the spinner loader in place of the label and blocks pointer events.
 * @prop {Boolean} isIcon - When true, always renders the (icon) slot regardless of `name`.
 * @prop {String} iconClass - Unused class hook reserved for icon styling by callers.
 * @prop {Boolean} isSearch - Applies search-button-specific layout (attached-to-input styling).
 * @prop {String} form - Native `form` attribute, associating the button with a form by id.
 * @prop {String} color - Unused/reserved color override prop (not applied in the template).
 */
const props = defineProps({
  name: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "button",
    // Accept only valid button types; Vue DOM typings expect this union
    validator: /** @param {unknown} v */ (v) => typeof v === 'string' && (v === 'button' || v === 'submit' || v === 'reset'),
  },
  mode: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isIcon: {
    type: Boolean,
    default: false,
  },
  iconClass: {
    type: String,
    default: "",
  },
  isSearch: {
    type: Boolean,
    default: false,
  },
  form: {
    type: String,
    default: "",
  },
  color: String,
});

/** Computes the button's variant/state classes (disabled, bordered/text/secondary mode, search, loading) from props. */
const btnClass = computed(() => {
  return [
    {
      "new-base-btn_disabled": props.disabled,
      "new-base-btn_bordered": props.mode === "bordered",
      "new-base-btn_text": props.mode === "text",
      "new-base-btn_secondary": props.mode === "secondary",
      "new-base-btn_is-search": props.isSearch,
      "new-base-btn_is-loading": props.isLoading,
    },
  ];
});

// Narrow the type prop to a valid union for DOM typings
/** Narrows `props.type` to a valid native button type union, defaulting to 'button' for any unexpected value. */
const typedButtonType = computed(() => (props.type === 'submit' || props.type === 'reset' || props.type === 'button') ? props.type : 'button')
</script>

<style lang="scss" scoped>
.new-base-btn {
  padding: 5px 10px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  // background: var(--color-brand-secondary);
  // background: var(--color-primary-button-bg);
  background: var(--color-accent-primary);
  border-radius: 5.8px !important;
  border: 1px solid var(--color-button-border);
  // color: var(--color-button-on-primary);
  // border-radius: 8px;
  // color: #09090B;
  color: var(--color-text-black-white);
  // color: #ffffff;
  min-width: 106px;
  // max-height: 40px;
  // height: 44px;
  max-height: 40px;
  height: 40px;
  font-size: 14px;
  // font-family: 'SFProText', serif;
  font-family: $poppins;
  font-weight: 500 !important;
  transition: all 0.3s ease;
  -webkit-tap-highlight-color: transparent;
  position: relative;

  &:hover {
    // opacity: 0.7;
  }

  &--extra-small {
    max-height: 32px;
    padding: 6px 4px;
  }

  &--small {
    max-height: 40px;
    min-width: 100%;
    padding: 8px 6px;
    font-size: 14px;

    @include _575 {
      font-size: 14px !important;
    }

    @include _380 {
      font-size: 14px !important;
      padding: 8px 6px !important;
    }
  }

  // &__name {
  //   color: white;
  //   display: inline-block;
  //   margin-right: 0;
  //   position: absolute;
  //   left: 50%;
  //   transform: translateX(-50%);
  //   font-weight: 400;
  // }

  // &__loader {
  //   width: 18px;
  //   height: 18px;
  //   color: #ffffff;
  //   border: 2px solid currentcolor;
  //   border-bottom-color: transparent;
  //   border-radius: 50%;
  //   animation: 1s loader linear infinite;
  //   position: absolute;
  //   left: 50%;
  //   top: 50%;
  //   transform: translate(-50%, -50%);
  // }
  &__name {
    /* OLD: color: #000; */
    color: var(--color-text-black-white);
    display: inline-block;
    margin-right: 0;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    font-weight: 600;
  }

  &__loader {
    width: 18px;
    height: 18px;
    /* OLD: color: #000; */
    color: var(--color-text-black-white);
    border: 2px solid currentcolor;
    border-bottom-color: transparent;
    border-radius: 50%;
    animation: 1s loader linear infinite;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  @keyframes loader {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
    }

    100% {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }

  &__slot {
    text-align: center;
  }

  &_disabled {
    background: var(--color-background-secondary);
    color: var(--color-text-white-black) !important;
    pointer-events: none;
    cursor: no-drop;
  }

  &_bordered {
    border: 1px solid var(--color-foreground-primary);
    background: transparent;
    color: var(--color-foreground-primary);
  }

  &_text {
    padding: 0;
    min-width: auto;
    background: transparent;
    color: var(--color-brand-secondary);
    transition: 0.3s ease-in-out;
  }

  &_secondary {
    background: var(--color-background-btn-secondary);
    color: var(--color-foreground-primary);
    transition: 0.3s ease-in-out;
  }

  &_is-search {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-left: 15px;
    margin-right: -16px;

    .new-base-btn__name {
      margin-right: 10px;
    }
  }

  &_is-loading {
    pointer-events: none;
  }

  @include _575 {
    font-size: 18px;
  }

  @include _380 {
    font-size: 16px;
    padding: 10px 15px;
  }
}

/* In dark mode, keep black text on the default green button (e.g., Unfollow)
   without affecting bordered/secondary/text variants */
// [scheme='dark']
.new-base-btn:not(.new-base-btn_secondary):not(.new-base-btn_bordered):not(.new-base-btn_text) {
  // color: #09090B;
  color: var(--color-text-black-white);
}
</style>
